// store.jsx — CardAttachmentsContext (UI state + acciones)
// Requiere estar dentro de QuotationByIdMultiRoleProvider (padre)
import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { useQuotationByIdMultiRole } from "../store"; // 👈 MISMO módulo padre

export const CardAttachmentsContext = createContext(null);

export function useCardAttachments() {
  const ctx = useContext(CardAttachmentsContext);
  if (!ctx) throw new Error("useCardAttachments must be used within CardAttachmentsProvider");
  return ctx;
}

// Rutas backend
function makeAttPath(role, quotationId, kind, attId) {
  const base = `/${role}/api/quotations/${quotationId}/attachments`;
  if (kind === "create") return `${base}/create`;
  if (kind === "delete") return `${base}/delete/${attId}`;
  throw new Error("Unknown URL kind");
}

const MAX_ATTACHMENTS = 10;

export function CardAttachmentsProvider({ children }) {
  const { role, quotationId, quotation, refresh } = useQuotationByIdMultiRole();

  // UI local
  const [openModal, setOpenModal] = useState("none"); // "none" | "add" | "delete"
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Derivados
  const attachments = useMemo(
    () => (Array.isArray(quotation?.attachments) ? quotation.attachments : []),
    [quotation]
  );
  const count = attachments.length;
  const canAddMore = count < MAX_ATTACHMENTS;

  // UI actions
  const openAddModal = useCallback(() => setOpenModal("add"), []);
  const openDeleteModal = useCallback((id) => { if (id) setSelectedId(id); setOpenModal("delete"); }, []);
  const closeModal = useCallback(() => setOpenModal("none"), []);

  // Biz actions
  const addAttachments = useCallback(
    async (files) => {
      if (!quotationId) throw new Error("quotationId missing");
      if (!files || !files.length) return;
      setUploading(true);
      setError(null);
      try {
        // respeta el máximo
        const remaining = Math.max(0, MAX_ATTACHMENTS - attachments.length);
        const toSend = Array.from(files).slice(0, remaining);

        const fd = new FormData();
        for (const f of toSend) fd.append("files", f, f.name); // como en creación de cotización
        await fetchToServer({
          url: makeAttPath(role, quotationId, "create"),
          method: "post",
          data: fd,
          withCredentials: true,
          headers: { /* axios set boundary automáticamente */ },
        });
        await refresh();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setUploading(false);
      }
    },
    [role, quotationId, attachments.length, refresh]
  );

  const deleteAttachment = useCallback(
    async (id) => {
      const targetId = id || selectedId;
      if (!quotationId) throw new Error("quotationId missing");
      if (!targetId) throw new Error("attachmentId missing");
      setDeleting(true);
      setError(null);
      try {
        await fetchToServer({
          url: makeAttPath(role, quotationId, "delete", targetId),
          method: "delete",
          withCredentials: true,
        });
        if (selectedId === targetId) setSelectedId(null);
        await refresh();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setDeleting(false);
      }
    },
    [role, quotationId, selectedId, refresh]
  );

  const value = useMemo(
    () => ({
      // datos padre
      quotation, role, quotationId,
      // derivados
      attachments, count, canAddMore, MAX_ATTACHMENTS,
      // ui
      openModal, selectedId, uploading, deleting, error,
      // ui actions
      setSelectedId, openAddModal, openDeleteModal, closeModal,
      // biz
      addAttachments, deleteAttachment,
    }),
    [
      quotation, role, quotationId,
      attachments, count, canAddMore,
      openModal, selectedId, uploading, deleting, error,
      openAddModal, openDeleteModal, closeModal, addAttachments, deleteAttachment
    ]
  );

  return (
    <CardAttachmentsContext.Provider value={value}>
      {children}
    </CardAttachmentsContext.Provider>
  );
}
